import React, {
  useEffect,
  useLayoutEffect,
  useContext,
  useState,
  useRef,
} from "react";
import "./App.css";
import { useAuth0 } from "@auth0/auth0-react";
import UserContext from "./Contexts/UserContext/UserContext";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Components/PageShell/pageshell.css";
import Loading from "./Components/Loading";
import PageHeading from "./Components/PageShell/PageHeading";
import userService from "./Services/userService";
import { SubjectsProvider } from "./Contexts/SubjectsContext/SubjectsProvider";
import { RoutersProvider } from "./Contexts/RoutersContext/RoutersProvider";
import { ToastProvider } from "./Contexts/ToastContext/ToastProvider";
import { LiveAnnouncerProvider } from "./Components/LiveAnnouncer/LiveAnnouncer";
import { useLocation } from "react-router-dom"; // Assuming you use react-router-dom
import PrivacyPolicyRouteComponent from "./Routes/PrivacyPolicyRouteComponent";
import NoAccessRouteComponent from "./Routes/NoAccessRouteComponent";
import { setupAxiosInterceptors } from "./Services/apiClient";
import { clearSessionTraces, restartSignedOut } from "./Services/sessionReset";
import DesktopAuthCompletedRouteComponent from "./Routes/DesktopAuthCompletedRouteComponent";
import DesktopSignoutRoute from "./Routes/DesktopSignoutRoute";

// Lazy-loaded route components. Import thunks are shared so the chunk
// download can be kicked off before the component first renders.
const loadSuperUserRoute = () => import("./Routes/SuperUserRouteComponent");
const loadUserRoute = () => import("./Routes/UserRouteComponent");
const SuperUserRouteComponent = React.lazy(loadSuperUserRoute);
const UserRouteComponent = React.lazy(loadUserRoute);
const CLAIMS_NAMESPACE = "https://claims.4sightlabs.com";

function App() {
  const { userData, setUserData, setAccessToken, resetSession } =
    useContext(UserContext);
  const {
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    getIdTokenClaims,
    error,
    logout,
    loginWithRedirect,
    user,
  } = useAuth0();
  const location = useLocation();

  const sessionSub = isAuthenticated ? user?.sub : undefined;
  const userDataIsCurrent =
    !!sessionSub && userData?.userInfo?.id === sessionSub;
  const [userNotFoundFor, setUserNotFoundFor] = useState(null);
  const sessionOwner = useRef(null);
  const onDesktopRelayRoute =
    location.pathname.startsWith("/desktop-auth-completed") ||
    location.pathname.startsWith("/desktop-signout");

  const claimRole = user?.[`${CLAIMS_NAMESPACE}/role`];
  const claimEntityId = user?.[`${CLAIMS_NAMESPACE}/entityId`];
  const claimPermissions = user?.[`${CLAIMS_NAMESPACE}/permissions`];
  const hasClaimAccess = !!(
    claimRole ||
    claimEntityId ||
    (Array.isArray(claimPermissions) && claimPermissions.length > 0)
  );

  const claimsRefreshAttempted = useRef(false);
  const [claimsResolved, setClaimsResolved] = useState(false);

  useEffect(() => {
    const cleanup = setupAxiosInterceptors(
      getAccessTokenSilently,
      setAccessToken,
      () => {
        clearSessionTraces();
        logout({ logoutParams: { returnTo: window.location.origin } });
      },
    );

    return cleanup;
  }, [getAccessTokenSilently, setAccessToken, logout]);

  // Warm the role-appropriate route chunk as soon as auth resolves so it
  // downloads in parallel with getUserInfo. Rendering still waits on the
  // backend role below — dynamic import() is cached, so React.lazy reuses
  // this download instead of starting it after userData arrives.
  useEffect(() => {
    if (!isAuthenticated || !claimRole) return;
    if (claimRole === "4SightAdmin") {
      loadSuperUserRoute();
    } else {
      loadUserRoute();
    }
  }, [isAuthenticated, claimRole]);

  useEffect(() => {
    const currentPath = location.pathname;
    const isPrivacyPolicyRoute = currentPath.startsWith("/privacypolicy");
    const isAccessDenied = currentPath.startsWith("/?error=access_denied");
    const isDesktopAuthCompletedRoute = currentPath.startsWith(
      "/desktop-auth-completed",
    );
    const isDesktopSignoutRoute = currentPath.startsWith("/desktop-signout");

    if (
      !isLoading &&
      !isAuthenticated &&
      !isPrivacyPolicyRoute &&
      !isAccessDenied &&
      !isDesktopAuthCompletedRoute &&
      !isDesktopSignoutRoute
    ) {
      if (currentPath !== "/")
        sessionStorage.setItem("redirectPath", currentPath);

      const storedEmail = localStorage.getItem("user_email") || "";
      const domain = storedEmail.split("@")[1]?.toLowerCase() || "";
      const is4Sight = domain === "4sightlabs.com";

      const options =
        storedEmail && !is4Sight
          ? {
              authorizationParams: {
                login_hint: storedEmail,
              },
            }
          : {};

      loginWithRedirect(options);
    }
  }, [isLoading, isAuthenticated, location.pathname, loginWithRedirect]);

  useLayoutEffect(() => {
    const isDark = userData?.userInfo?.darkTheme;
    document.body.classList.toggle("dark-theme", !!isDark);
    try {
      localStorage.setItem("ow-dark-theme", isDark ? "true" : "false");
    } catch (e) {}
  }, [userData?.userInfo?.darkTheme]);

  const hasUserInfo = !!userData?.userInfo;
  const reduceMotion = !!userData?.userInfo?.reduceMotion;
  useLayoutEffect(() => {
    if (!hasUserInfo) return;
    document.body.classList.toggle("reduce-motion", reduceMotion);
    try {
      localStorage.setItem("ow-reduce-motion", reduceMotion ? "true" : "false");
    } catch (e) {}
  }, [hasUserInfo, reduceMotion]);

  // If the session has no provisioning claims, force ONE token refresh. The
  // refresh-token exchange re-runs the Auth0 post-login Action, replaces the
  // cached ID token, and updates `user`. Heals tokens minted before the
  // Action set ID-token claims, and retries a transient getUserPermissions
  // failure from the original login.
  // Declared BEFORE the getUserInfo effect: the SDK coalesces concurrent
  // getAccessTokenSilently calls regardless of cacheMode, so the forced
  // refresh must register first for the axios interceptor's call to
  // piggyback on the refresh instead of the reverse.
  useEffect(() => {
    if (!isAuthenticated || hasClaimAccess) return;
    if (claimsRefreshAttempted.current) return;

    const currentPath = location.pathname;
    if (
      currentPath.startsWith("/privacypolicy") ||
      currentPath.startsWith("/desktop-auth-completed") ||
      currentPath.startsWith("/desktop-signout")
    )
      return;

    claimsRefreshAttempted.current = true;
    getAccessTokenSilently({ cacheMode: "off" })
      .catch((refreshError) => {
        // SDK throws here (never dispatches to `error`), so this cannot trip
        // the invalid_grant logout block above. Auth failures repeat on the
        // next API call and are handled by the axios interceptor's logout.
        console.error("Claims token refresh failed:", refreshError);
      })
      .finally(() => setClaimsResolved(true));
  }, [
    isAuthenticated,
    hasClaimAccess,
    location.pathname,
    getAccessTokenSilently,
  ]);

  // Diagnostic: backend vouches for the user but the token has no claims —
  // this user is locked out by the strict gate. If this fires for legit
  // users, the Action/getUserPermissions side needs fixing.
  useEffect(() => {
    if (claimsResolved && !hasClaimAccess && userData?.userInfo?.userRole) {
      console.warn(
        "Backend role present but token claims are empty for",
        user?.sub,
      );
    }
  }, [claimsResolved, hasClaimAccess, userData?.userInfo?.userRole, user?.sub]);

  useEffect(() => {
    if (isLoading) return;
    const previous = sessionOwner.current;
    const next = sessionSub || null;
    sessionOwner.current = next;
    if (previous === next) return;
    resetSession();
    if (previous && !location.pathname.startsWith("/desktop-signout"))
      restartSignedOut();
  }, [isLoading, sessionSub, resetSession, location.pathname]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== null && !event.key.startsWith("@@auth0spajs@@")) return;
      const owner = sessionOwner.current;
      if (!owner) return;
      Promise.resolve(getIdTokenClaims())
        .then((claims) => {
          if (claims?.sub !== owner) restartSignedOut();
        })
        .catch(() => {});
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [getIdTokenClaims]);

  // Fetch user data after login
  useEffect(() => {
    if (!sessionSub || onDesktopRelayRoute) return undefined;
    let cancelled = false;

    userService
      .getUserInfo()
      .then((response) => {
        if (cancelled || response.data?.userInfo?.id !== sessionSub) return;
        setUserData(response.data);
        if (response.data.userInfo.email) {
          localStorage.setItem("user_email", response.data.userInfo.email);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        if (
          error?.response?.data === "User's info is not found" &&
          error?.response?.status === 404
        ) {
          setUserNotFoundFor(sessionSub);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [sessionSub, onDesktopRelayRoute, setUserData]);

  if (location.pathname.startsWith("/privacypolicy"))
    return <PrivacyPolicyRouteComponent />;

  if (location.pathname.startsWith("/desktop-auth-completed"))
    return <DesktopAuthCompletedRouteComponent />;

  if (location.pathname.startsWith("/desktop-signout"))
    return <DesktopSignoutRoute />;

  if (error) {
    const msg = error.message || "";
    const isInvalidGrant =
      msg.includes("Access denied") ||
      msg.includes("domain restriction") ||
      msg.includes("invalid_grant") ||
      msg.includes("Unknown or invalid refresh token") ||
      msg.includes("Missing Refresh Token") ||
      error.error === "invalid_grant" ||
      error.message === "Access denied. IP address not allowed.";

    if (isInvalidGrant) {
      clearSessionTraces();
      logout({
        logoutParams: {
          returnTo: window.location.origin,
          federated: true,
        },
      });
      return <Loading />;
    }
    return (
      <main>
        <PageHeading documentTitle="Sign-in error">
          {msg || "oops. something went wrong"}
        </PageHeading>
      </main>
    );
  }

  // Handle user not found
  if (isAuthenticated && userNotFoundFor === sessionSub)
    return <NoAccessRouteComponent />;

  // Strict claims gate: no/empty custom claims means no access. Show Loading
  // until the one-shot forced refresh has resolved, then NoAccess.
  if (isAuthenticated && !isLoading && !hasClaimAccess)
    return claimsResolved ? <NoAccessRouteComponent /> : <Loading />;

  // Show loading state
  if (
    isLoading ||
    !isAuthenticated ||
    !userDataIsCurrent ||
    // !accessToken ||
    !userData?.userInfo?.userRole
  )
    return <Loading />;

  // Role-based rendering for authenticated users
  const role = userData?.userInfo?.userRole;
  return (
    <RoutersProvider>
      <SubjectsProvider>
        <LiveAnnouncerProvider>
          <ToastProvider>
            <React.Suspense fallback={<Loading />}>
              {role === "4SightAdmin" ? (
                <SuperUserRouteComponent />
              ) : role ? (
                <UserRouteComponent />
              ) : (
                <NoAccessRouteComponent />
              )}
            </React.Suspense>
          </ToastProvider>
        </LiveAnnouncerProvider>
      </SubjectsProvider>
    </RoutersProvider>
  );
}

export default App;
